import React, { useState, useRef, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { PlayIcon, PauseIcon, DownloadIcon } from 'lucide-react';

interface AudioPlayerProps {
  audioData?: ReadableStream;
  audioDataUrl?: string | null;
  download?: boolean;
  audioBar?: boolean;
  playButton?: boolean;
  disabled?: boolean;
}

const AudioPlayer: React.FC<AudioPlayerProps> = ({
  audioDataUrl,
  download = false,
  audioBar = false,
  playButton = false,
  disabled = false,
}) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [audioUrl, setAudioUrl] = useState<string | null>(audioDataUrl || null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!audioDataUrl) {
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);
    const objectUrl: string | null = null;

    const processStream = async () => {
      try {
        if (audioDataUrl) {
          setAudioUrl(audioDataUrl);
          setIsLoading(false);
          return;
        }
      } catch (e) {
        console.error('Error processing audio stream:', e);
        setError(`Failed to load audio: ${e}`);
      } finally {
        setIsLoading(false);
      }
    };

    processStream();

    return () => {
      // On cleanup, revoke the object URL to prevent memory leaks.
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
    };
  }, [audioDataUrl]);

  const togglePlayPause = () => {
    if (isPlaying) {
      audioRef.current?.pause();
      setIsPlaying(false);
    } else {
      audioRef.current?.play();
      setIsPlaying(true);
    }
  };

  if (isLoading) {
    return <div>Loading audio...</div>;
  }

  if (error) {
    return <div style={{ color: 'red' }}>{error}</div>;
  }

  if (!audioUrl) {
    return null;
  }

  return (
    <div className='flex flex-row items-center gap-4'>
      {audioBar && (
        <audio
          ref={audioRef}
          src={audioUrl}
          onPlay={() => setIsPlaying(true)}
          onPause={() => setIsPlaying(false)}
          onEnded={() => setIsPlaying(false)}
          preload='auto'
          controls={true}
        />
      )}
      {playButton && (
        <>
          <Button
            size='icon'
            variant='ghost'
            className='flex items-center gap-2'
            onClick={togglePlayPause}
            disabled={disabled}
          >
            {isPlaying ? (
              <PauseIcon className='w-2 h-2' />
            ) : (
              <PlayIcon className='w-2 h-2' />
            )}
          </Button>
          <audio
            ref={audioRef}
            src={audioUrl}
            onPlay={() => setIsPlaying(true)}
            onPause={() => setIsPlaying(false)}
            onEnded={() => setIsPlaying(false)}
            preload='auto'
            style={{ display: 'none' }}
          />
        </>
      )}
      {download && (
        <Button
          size='sm'
          variant='outline'
          className='flex items-center gap-2'
          onClick={() => {
            const a = document.createElement('a');
            a.href = audioUrl;
            a.download = 'audio.mp3';
            a.click();
          }}
        >
          <DownloadIcon className='w-4 h-4' />
        </Button>
      )}
    </div>
  );
};

export default AudioPlayer;
